// Central platsregister – sanningskälla för var Fenny faktiskt
// är representerat och hur ortssidor får beskriva närvaron.
// FEN-009: LocalBusiness-schema får ENDAST emitteras för platser
// av typ "office". Övriga får Service med areaServed.
//
// Tomma fält som postaladdress / telefon på offices markeras
// `pendingApproval: true` tills affären lämnar slutgiltigt svar
// (se docs/business-decisions-pending.md).

export type LocationType =
  | "office" // fysiskt kontor med adress vi äger eller hyr
  | "local_team" // teammedlemmar bosatta på orten, ingen publik adress
  | "partner_network" // samarbete med lokal fotograf/producent
  | "service_area"; // ingen lokal närvaro – täcks från annan ort

export type LocationEntity = {
  /** Kebab-case slug, matchar /produktionsbolag/<slug> osv. */
  slug: string;
  /** Visningsnamn ("Stockholm", "Göteborg"). */
  city: string;
  type: LocationType;
  /** Stad som faktiskt levererar när type !== "office". */
  servedFrom?: string;
  /** Postadress används bara för office. */
  postalAddress?: {
    streetAddress: string;
    postalCode: string;
    addressLocality: string;
    addressCountry: "SE";
  };
  /** Publikt telefonnummer för plats (ofta huvudnumret). */
  telephone?: string;
  /** Geolokalisering (för office). */
  geo?: { latitude: number; longitude: number };
  /** Affärsbeslut saknas – fyll i värden men exponera ej i schema. */
  pendingApproval?: boolean;
};

// NOTE: faktiska adresser/telefoner kräver affärsgodkännande – se
// docs/business-decisions-pending.md. Strukturen sätts nu så
// sidorna/schemat kan kopplas in så fort värden finns.

export const LOCATIONS: Record<string, LocationEntity> = {
  stockholm: {
    slug: "stockholm",
    city: "Stockholm",
    type: "office",
    // Bekräftad besöksadress. Postnummer inväntar bekräftelse och lämnas
    // tomt tills dess — tomma fält emitteras inte i schema.
    postalAddress: {
      streetAddress: "Bjurholmsplan 24",
      postalCode: "",
      addressLocality: "Stockholm",
      addressCountry: "SE",
    },
    telephone: "+46 8 480 047 06",
    geo: { latitude: 59.3078, longitude: 18.0797 },
  },
  vasteras: {
    slug: "vasteras",
    city: "Västerås",
    type: "office",
    pendingApproval: true,
  },
  malmo: {
    slug: "malmo",
    city: "Malmö",
    type: "office",
    pendingApproval: true,
  },
  goteborg: {
    slug: "goteborg",
    city: "Göteborg",
    type: "local_team",
    servedFrom: "Stockholm",
  },
  uppsala: { slug: "uppsala", city: "Uppsala", type: "service_area", servedFrom: "Stockholm" },
  solna: { slug: "solna", city: "Solna", type: "service_area", servedFrom: "Stockholm" },
  linkoping: { slug: "linkoping", city: "Linköping", type: "service_area", servedFrom: "Stockholm" },
  norrkoping: { slug: "norrkoping", city: "Norrköping", type: "service_area", servedFrom: "Stockholm" },
  eskilstuna: { slug: "eskilstuna", city: "Eskilstuna", type: "service_area", servedFrom: "Västerås" },
  orebro: { slug: "orebro", city: "Örebro", type: "service_area", servedFrom: "Västerås" },
  gavle: { slug: "gavle", city: "Gävle", type: "service_area", servedFrom: "Stockholm" },
  sundsvall: { slug: "sundsvall", city: "Sundsvall", type: "service_area", servedFrom: "Stockholm" },
  umea: { slug: "umea", city: "Umeå", type: "service_area", servedFrom: "Stockholm" },
  lulea: { slug: "lulea", city: "Luleå", type: "service_area", servedFrom: "Stockholm" },
  jonkoping: { slug: "jonkoping", city: "Jönköping", type: "service_area", servedFrom: "Malmö" },
  vaxjo: { slug: "vaxjo", city: "Växjö", type: "service_area", servedFrom: "Malmö" },
  halmstad: { slug: "halmstad", city: "Halmstad", type: "service_area", servedFrom: "Malmö" },
  helsingborg: { slug: "helsingborg", city: "Helsingborg", type: "service_area", servedFrom: "Malmö" },
  lund: { slug: "lund", city: "Lund", type: "service_area", servedFrom: "Malmö" },
  boras: { slug: "boras", city: "Borås", type: "service_area", servedFrom: "Stockholm" },
  karlstad: { slug: "karlstad", city: "Karlstad", type: "service_area", servedFrom: "Västerås" },
};

export const OFFICES = Object.values(LOCATIONS).filter((l) => l.type === "office");

export function getLocation(slug: string): LocationEntity | undefined {
  return LOCATIONS[slug];
}

export function isOffice(slug: string): boolean {
  return LOCATIONS[slug]?.type === "office";
}
